import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardMedia,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import ProfileDeletePhotoApi from "../../../api_calls/ProfileDeletePhotoApi";
import SetProfilePhotoApi from "../../../api_calls/SetProfilePhotoApi";
import { showSuccess, showError } from "../../../utils/toastUtils";

export default function MyPhotosTab({
  profile,
  openLightbox,
  triggerOtherFileInput,
  onProfilePhotoChanged,
}) {
  const [localPhotos, setLocalPhotos] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedPhotoId, setSelectedPhotoId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [promoteId, setPromoteId] = useState(null);
  const [promoting, setPromoting] = useState(false);

  useEffect(() => {
    if (profile?.photos) {
      setLocalPhotos(profile.photos);
    }
  }, [profile]);

  const openDeleteDialog = (photoId) => {
    setSelectedPhotoId(photoId);
    setConfirmOpen(true);
  };

  const closeDeleteDialog = () => {
    setConfirmOpen(false);
    setSelectedPhotoId(null);
  };

  const confirmDeletePhoto = async () => {
    if (!selectedPhotoId) return;

    setDeletingId(selectedPhotoId);

    const res = await ProfileDeletePhotoApi.deletePhoto(selectedPhotoId);

    if (res?.status === "success") {
      showSuccess("Photo deleted successfully");
      setLocalPhotos((prev) => prev.filter((p) => p.id !== selectedPhotoId));
    } else {
      showError(res?.data || "Failed to delete photo");
    }

    setDeletingId(null);
    closeDeleteDialog();
  };

  const confirmSetProfilePhoto = async () => {
    if (!promoteId) return;
    setPromoting(true);

    const res = await SetProfilePhotoApi.fromGallery(promoteId);

    if (res?.status === "success") {
      showSuccess("Profile photo updated");
      if (onProfilePhotoChanged) onProfilePhotoChanged(res.data.profile_pic);
    } else {
      const reasons = {
        photo_flagged: "This photo was flagged and can't be used as a profile photo.",
        photo_not_found: "That photo no longer exists.",
      };
      showError(reasons[res?.data?.message] || "Couldn't update your profile photo. Please try again.");
    }

    setPromoting(false);
    setPromoteId(null);
  };

  const photoActionSx = {
    position: "absolute",
    bottom: 8,
    background: "rgba(0,0,0,0.6)",
    color: "white",
    transition: "0.2s",
  };

  return (
    <>
      {/* MAX PHOTO NOTICE */}
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#616161",
          }}
        >
          You can upload a maximum of <strong>8 photos</strong>.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "left",
        }}
      >
        {localPhotos?.length > 0 ? (
          <>
            {localPhotos.map((photo, index) => (
              <Card
                key={photo.id}
                sx={{
                  width: { xs: "100%", sm: "48%", md: "31%", lg: "23%" },
                  borderRadius: "10px",
                  overflow: "hidden",
                  position: "relative",
                  transition: "0.25s",
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={photo.url}
                  alt={`Photo ${photo.id}`}
                  sx={{
                    objectFit: "cover",
                    width: "100%",
                    height: "100%",
                    cursor: "pointer",
                  }}
                  onClick={() => openLightbox(index)}
                />

                <Tooltip title="Set as profile photo">
                  <IconButton
                    aria-label="Set as profile photo"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPromoteId(photo.id);
                    }}
                    sx={{
                      ...photoActionSx,
                      right: 52,
                      "&:hover": { background: "#2e7d32" },
                    }}
                  >
                    <AccountCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Delete photo">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteDialog(photo.id);
                    }}
                    sx={{
                      position: "absolute",
                      bottom: 8,
                      right: 8,
                      background: "rgba(0,0,0,0.6)",
                      color: "white",
                      opacity: 1,
                      transition: "0.2s",
                      "&:hover": {
                        background: "#c62828",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Card>
            ))}

            {/* ADD PHOTO */}
            {localPhotos.length < 8 && (
              <Typography
                component="div"
                onClick={triggerOtherFileInput}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  gap: "8px",
                  fontWeight: 400,
                  fontSize: { xs: 12, sm: 13, md: 14 },
                  color: "#757575",
                  p: 1,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    color: "#424242",
                    transform: "scale(1.05)",
                  },
                }}
              >
                <AddPhotoAlternateIcon sx={{ fontSize: "1rem" }} />
                Add Photo
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography>No photos available</Typography>

            <Typography
              component="div"
              onClick={triggerOtherFileInput}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                gap: "8px",
                fontWeight: 400,
                fontSize: { xs: 12, sm: 13, md: 14 },
                color: "#757575",
                p: 1,
                border: "1px dashed #e0e0e0",
                borderRadius: "10px",
                width: { xs: "100%", sm: "48%", md: "31%", lg: "23%" },
                transition: "all 0.2s ease",
                "&:hover": {
                  color: "#424242",
                  borderColor: "#bdbdbd",
                  transform: "scale(1.05)",
                },
              }}
            >
              <AddPhotoAlternateIcon sx={{ fontSize: "1rem" }} />
              Add Photo
            </Typography>
          </>
        )}
      </Box>

      {/* CONFIRM DELETE DIALOG */}
      <Dialog open={confirmOpen} onClose={closeDeleteDialog}>
        <DialogTitle sx={{ fontWeight: "bold" }}>Delete photo?</DialogTitle>

        <DialogContent>
          <DialogContentText>
            This photo will be permanently removed from your profile. This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deletingId}>
            Cancel
          </Button>

          <Button
            onClick={confirmDeletePhoto}
            color="error"
            variant="contained"
            disabled={deletingId}
            startIcon={<DeleteIcon />}
          >
            {deletingId ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CONFIRM SET AS PROFILE PHOTO DIALOG */}
      <Dialog open={Boolean(promoteId)} onClose={() => !promoting && setPromoteId(null)}>
        <DialogTitle sx={{ fontWeight: "bold" }}>Use as profile photo?</DialogTitle>

        <DialogContent>
          <DialogContentText>
            This photo will replace your current profile photo. It will also
            stay in your gallery.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPromoteId(null)} disabled={promoting}>
            Cancel
          </Button>

          <Button
            onClick={confirmSetProfilePhoto}
            color="success"
            variant="contained"
            disabled={promoting}
            startIcon={<AccountCircleIcon />}
          >
            {promoting ? "Updating..." : "Use as profile photo"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
